import React, { useMemo, useState } from "react";
import { Mafs, Coordinates, Vector, Point, Theme } from "mafs";
import { Card, Space, Typography, Segmented, Tag, Alert, Row } from "antd";
import { AimOutlined, UserOutlined, CheckCircleOutlined, BulbOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

export const VectorAddition = ({ config, content }) => {
    const cfg = config || {};
    const explanation = content?.explanation || "Add vectors using the head-to-tail method.";
    const hasStudent = !!cfg.studentAnswerVector;
    const [viewMode, setViewMode] = useState("CORRECT");

    const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
    const asNum = (v, fallback = 0) => {
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? n : fallback;
    };

    const showComponents = cfg.showComponents !== false;
    const showResultant = cfg.showResultant !== false;
    const interactive = cfg.interactive !== false;

    const units = cfg.units ? String(cfg.units) : "";
    const withUnits = (s) => (units ? `${s} ${units}` : s);

    const baseVectors = Array.isArray(cfg.vectors) ? cfg.vectors.filter(Boolean) : [];

    const correctSum = cfg.vector || { x: 0, y: 0 };
    const studentSum = cfg.studentAnswerVector || { x: 0, y: 0 };
    const activeSum = viewMode === "STUDENT" ? studentSum : correctSum;

    const activeX = asNum(activeSum?.x, 0);
    const activeY = asNum(activeSum?.y, 0);
    const correctX = asNum(correctSum?.x, 0);
    const correctY = asNum(correctSum?.y, 0);

    const chain = useMemo(() => {
        let currentPos = [0, 0];
        return baseVectors.map((v) => {
            const dx = asNum(v?.x, 0);
            const dy = asNum(v?.y, 0);
            const start = [...currentPos];
            currentPos = [currentPos[0] + dx, currentPos[1] + dy];
            return { start, end: [...currentPos], color: v?.color };
        });
    }, [baseVectors]);

    const activeLength = Math.hypot(activeX, activeY);
    const correctLength = Math.hypot(correctX, correctY);

    // Clamp gridRange to keep Mafs stable
    const gridRange = clamp(asNum(cfg.gridRange, 10), 3, 30);

    return (
        <div className="ch-vector-widget animate-slide-up">
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
                <Card size="small" className="ch-shapeshifter-header">
                    <Row justify="space-between" align="middle">
                        <Space>
                            <Title level={4} style={{ margin: 0 }}>Vector Addition</Title>
                            {hasStudent && (
                                <Segmented
                                    value={viewMode}
                                    onChange={setViewMode}
                                    options={[
                                        { label: "Correct", value: "CORRECT", icon: <CheckCircleOutlined /> },
                                        { label: "Yours", value: "STUDENT", icon: <UserOutlined /> },
                                    ]}
                                />
                            )}
                        </Space>
                        <Space>
                            {showComponents && <Tag color="blue">Head-to-tail</Tag>}
                            {showResultant && <Tag color="green">Resultant</Tag>}
                        </Space>
                    </Row>
                </Card>

                <Text type="secondary">{explanation}</Text>

                <div className="ch-vector-layout" style={{ display: "flex", gap: "24px" }}>
                    <div style={{ flex: 1, border: "1px solid #d9d9d9", borderRadius: "12px", background: "#fff", padding: "10px" }}>
                        <Mafs
                            height={400}
                            viewBox={{ x: [-gridRange, gridRange], y: [-gridRange, gridRange] }}
                            pan={interactive}
                            zoom={interactive}
                        >
                            <Coordinates.Cartesian
                                // Avoid the “labels must be a function” pitfall
                                xAxis={{ labels: () => "" }}
                                yAxis={{ labels: () => "" }}
                            />

                            {showComponents && chain.map((v, i) => (
                                <Vector key={i} tail={v.start} tip={v.end} color={v.color || Theme.blue} weight={3} />
                            ))}

                            {showResultant && (
                                <Vector
                                    tail={[0, 0]}
                                    tip={[activeX, activeY]}
                                    color={viewMode === "STUDENT" ? "#fa8c16" : "#52c41a"}
                                    weight={5}
                                />
                            )}

                            {showResultant && viewMode === "STUDENT" && (
                                <Point x={correctX} y={correctY} color="#52c41a" opacity={0.5} />
                            )}
                        </Mafs>
                    </div>

                    <div style={{ width: "320px" }}>
                        <Space orientation="vertical" style={{ width: "100%" }}>
                            <div className="ch-fraction-metrics">
                                <div className="ch-fraction-metric-card">
                                    <Text type="secondary"><AimOutlined /> Magnitude</Text>
                                    <div className="ch-fraction-math" style={{ color: viewMode === "STUDENT" ? "#fa8c16" : "#1d39c4" }}>
                                        {withUnits(Number(activeLength.toFixed(2)))}
                                    </div>
                                    <Text type="secondary" style={{ fontSize: "11px" }}>
                                        Components: ({Number(activeX.toFixed(2))}, {Number(activeY.toFixed(2))})
                                    </Text>
                                </div>
                            </div>

                            {viewMode === "STUDENT" && (
                                <Alert
                                    message="Comparison"
                                    description={`Your length: ${activeLength.toFixed(2)}. Correct length: ${correctLength.toFixed(2)}.`}
                                    type="warning"
                                    showIcon
                                />
                            )}
                        </Space>
                    </div>
                </div>

                <Alert
                    title="Head-to-Tail Method"
                    description="Place the tail of the next vector at the head of the previous. The resultant (Sum) goes from the origin to the final tip."
                    type="info"
                    showIcon
                    icon={<BulbOutlined />}
                />
            </Space>
        </div>
    );
};
